import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Paper, Box, Button, 
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Alert,
  Grid, Stack
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProjectById } from '../api/projectService';
import { getTasksByProjectId, createTask, updateTask, deleteTask, Task } from '../api/taskService';

const ProjectTasksPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [project, setProject] = useState<any>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [open, setOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState<Partial<Task>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<number | null>(null);

  const fetchData = React.useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const id = parseInt(projectId);
      const [projectData, tasksData] = await Promise.all([
        getProjectById(id),
        getTasksByProjectId(id)
      ]);
      setProject(projectData);
      setTasks(tasksData);
    } catch (err: any) {
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = (task?: Task) => {
    if (task) {
      setCurrentTask({ ...task, due_date: task.due_date ? task.due_date.substring(0, 10) : '' });
      setIsEditing(true);
    } else {
      setCurrentTask({ project_id: parseInt(projectId!), title: '', description: '', status: 'To Do', priority: 'Medium', due_date: '' });
      setIsEditing(false);
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setCurrentTask({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentTask({ ...currentTask, [name]: value });
  };

  const handleSubmit = async () => {
    if (!projectId) return;
    try {
      if (isEditing && currentTask.id) {
        await updateTask(currentTask.id, currentTask);
        setSuccess('Task updated successfully');
      } else {
        await createTask(currentTask as Task);
        setSuccess('Task created successfully');
      }
      handleCloseDialog();
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(`Failed to save task: ${err.message}`);
    }
  };

  const confirmDelete = async () => {
    if (taskToDelete) {
      try {
        await deleteTask(taskToDelete);
        setSuccess('Task deleted successfully');
        fetchData();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(`Failed to delete task: ${err.message}`);
      }
    }
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  const canEdit = user && ['admin', 'editor'].includes(user.role);

  if (loading && !project) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 6, display: 'flex', alignItems: 'center', gap: 3 }}>
        <IconButton 
          onClick={() => navigate(`/projects/${projectId}`)} 
          sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' } }}
        >
          <BackIcon />
        </IconButton>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800 }}>Tasks: {project?.name}</Typography>
          <Typography variant="h6" color="text.secondary">Detailed project milestones and daily operations</Typography>
        </Box>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>{error}</Alert>}

      <Paper elevation={2} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 4, overflow: 'hidden' }}>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'flex-end', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.02)' }}>
          {canEdit && (
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => handleOpenDialog()}
              sx={{ borderRadius: 2, px: 3, fontWeight: 700 }}
            >
              New Task
            </Button>
          )}
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                <TableCell sx={{ fontWeight: 800, py: 2.5 }}>TASK DETAILS</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>STATUS</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>PRIORITY</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>DUE DATE</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((task) => (
                <TableRow key={task.id} hover>
                  <TableCell sx={{ py: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700}>{task.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>{task.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={task.status} 
                      size="small" 
                      color={task.status === 'Done' ? 'success' : 'primary'} 
                      sx={{ fontWeight: 700, borderRadius: 1.5 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={task.priority} 
                      size="small" 
                      variant="outlined" 
                      sx={{ fontWeight: 700, borderRadius: 1.5 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {canEdit && (
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton size="small" onClick={() => handleOpenDialog(task)} color="info" sx={{ bgcolor: 'rgba(2, 136, 209, 0.05)' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => { if (task.id) { setTaskToDelete(task.id); setDeleteDialogOpen(true); } }} color="error" sx={{ bgcolor: 'rgba(211, 47, 47, 0.05)' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialogs updated for consistency */}
      <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.5rem' }}>{isEditing ? 'Edit Task' : 'New Project Task'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField label="Title" name="title" fullWidth value={currentTask.title || ''} onChange={handleInputChange} required InputProps={{ sx: { borderRadius: 2 } }} />
            <TextField label="Description" name="description" fullWidth multiline rows={4} value={currentTask.description || ''} onChange={handleInputChange} InputProps={{ sx: { borderRadius: 2 } }} />
            <Grid container spacing={2}>
              <Grid {...{ item: true, xs: 6 }}>
                <TextField label="Status" name="status" select fullWidth value={currentTask.status || 'To Do'} onChange={handleInputChange} InputProps={{ sx: { borderRadius: 2 } }}>
                  {['To Do', 'In Progress', 'Blocked', 'Done'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid {...{ item: true, xs: 6 }}>
                <TextField label="Priority" name="priority" select fullWidth value={currentTask.priority || 'Medium'} onChange={handleInputChange} InputProps={{ sx: { borderRadius: 2 } }}>
                  {['Low', 'Medium', 'High', 'Critical'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <TextField label="Due Date" name="due_date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={currentTask.due_date || ''} onChange={handleInputChange} InputProps={{ sx: { borderRadius: 2 } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 4 }}>
          <Button onClick={handleCloseDialog} sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" size="large" sx={{ borderRadius: 2, px: 4, fontWeight: 800 }}>Save Task</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography sx={{ lineHeight: 1.6 }}>Are you sure you want to delete this task? This action is irreversible.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 4 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" sx={{ borderRadius: 2, px: 4, fontWeight: 800 }}>Delete Task</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectTasksPage;